/**
 * Constantes compartilhadas da aplicação
 * Centraliza valores que são usados em múltiplos módulos
 */

// Detectar se estamos em produção
// Usa flags do Vite e fallback para hosts não-locais
const isProduction =
  import.meta.env.PROD ||
  (typeof window !== "undefined" &&
    !window.location.hostname.includes("localhost:3000"));

// Obter URL do backend via variáveis de ambiente
const getBackendUrl = (): string => {
  // Em dev com proxy do Vite: mesma origem (/api) evita CORS e porta do front diferente de 3000
  const proxyFlag = String(import.meta.env.VITE_USE_PROXY ?? "")
    .trim()
    .replace(/^['"]|['"]$/g, "")
    .toLowerCase();
  if (import.meta.env.DEV && proxyFlag === "true") {
    return "/api";
  }

  // Priorizar URL completa (mesma abordagem do WhatsApp)
  let fullApiUrl = import.meta.env.VITE_APP_API_URL;
  // Em produção no browser: se a URL configurada for localhost, usar same-origin
  // (evita builds mal configurados em que produção ainda aponta para localhost)
  if (
    typeof window !== "undefined" &&
    fullApiUrl &&
    (fullApiUrl.includes("localhost") || fullApiUrl.includes("127.0.0.1")) &&
    !window.location.hostname.includes("localhost") &&
    !window.location.hostname.includes("127.0.0.1")
  ) {
    fullApiUrl = "";
  }
  if (fullApiUrl) {
    return fullApiUrl;
  }

  // Fallback para componentes separados (VITE_BACKEND_*)
  const backendHost =
    import.meta.env.VITE_BACKEND_HOST || import.meta.env.REACT_APP_BACKEND_HOST;
  const backendProtocol =
    import.meta.env.VITE_BACKEND_PROTOCOL ||
    import.meta.env.REACT_APP_BACKEND_PROTOCOL;
  const backendPort =
    import.meta.env.VITE_BACKEND_PORT || import.meta.env.REACT_APP_BACKEND_PORT;

  // Se variáveis de ambiente estão definidas, usar URL absoluta
  if (backendHost) {
    const protocol = backendProtocol || "http";
    const port = backendPort || (protocol === "https" ? "443" : "8000");

    // Normalizar 127.0.0.1 para localhost para evitar problemas de CORS
    // (navegadores tratam localhost e 127.0.0.1 como origens diferentes)
    const normalizedHost =
      backendHost === "127.0.0.1" ? "localhost" : backendHost;

    // Se não for localhost, assumir produção e não incluir porta se for HTTPS padrão
    const isDefaultPort = port === "443" && protocol === "https";
    const isLocalhost =
      normalizedHost === "localhost" || normalizedHost.startsWith("127.0.0.1");

    if (isLocalhost) {
      // Em localhost, sempre incluir porta
      return `${protocol}://${normalizedHost}:${port}/api`;
    } else {
      // Em produção remota, não incluir porta se for padrão
      return isDefaultPort
        ? `${protocol}://${normalizedHost}/api`
        : `${protocol}://${normalizedHost}:${port}/api`;
    }
  }

  // Em produção sem variáveis: usar URL relativa /api para que requisições
  // sejam same-origin e o nginx (ou outro proxy) encaminhe /api ao backend.
  // Se a API estiver em outro domínio, defina VITE_APP_API_URL no build.
  if (isProduction) {
    console.warn(
      "⚠️ [API CONFIG] Variáveis de ambiente não definidas em produção. Usando /api (same-origin). Defina VITE_APP_API_URL se a API estiver em outro domínio.",
    );
    return "/api";
  }

  // Em desenvolvimento, usar URL relativa (proxy do Vite cuida do redirecionamento)
  return "/api";
};

// URLs base da API
export const API_BASE_URL = getBackendUrl();
export const API_AUTH_URL = `${API_BASE_URL}/auth`;

/**
 * Helper para construir URLs de API
 * Em produção, usa URL absoluta. Em desenvolvimento, usa relativa (proxy cuida).
 *
 * @param endpoint - Endpoint da API (ex: '/api/stats' ou '/stats')
 * @returns URL completa (absoluta em produção, relativa em desenvolvimento)
 */
export const buildApiUrl = (endpoint: string): string => {
  // Se endpoint já começa com http, retornar como está
  if (endpoint.startsWith("http")) {
    return endpoint;
  }

  // Se API_BASE_URL é absoluta (produção), construir URL absoluta
  if (API_BASE_URL.startsWith("http")) {
    // API_BASE_URL = "http://localhost:8000/api"
    // endpoint = "/api/stats" ou "/stats"

    // Se endpoint já começa com /api, remover /api e concatenar
    if (endpoint.startsWith("/api")) {
      return `${API_BASE_URL}${endpoint.substring(4)}`; // Remove '/api' do início
    }

    // Se endpoint não começa com /api, adicionar
    const cleanEndpoint = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
    return `${API_BASE_URL}${cleanEndpoint}`;
  }

  // Em desenvolvimento, API_BASE_URL = "/api", usar relativa (proxy cuida)
  return endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
};

/** URL da imagem do post para exibição (preview, modal de melhorar imagem). */
export const getPostImageUrl = (imageInfo?: {
  filename?: string;
  public_url?: string;
}): string => {
  if (!imageInfo) return "";
  if (imageInfo.public_url)
    return imageInfo.public_url.startsWith("http")
      ? imageInfo.public_url
      : `${window.location.origin}${imageInfo.public_url}`;
  if (imageInfo.filename)
    return buildApiUrl(`/api/images/${imageInfo.filename}`);
  return "";
};

/** URL do vídeo do post ou Video Studio para exibição/download. */
export const getPostVideoUrl = (videoInfo?: {
  filename?: string;
  public_url?: string;
}): string => {
  if (!videoInfo) return "";
  if (videoInfo.public_url) {
    if (videoInfo.public_url.startsWith("http")) return videoInfo.public_url;
    if (videoInfo.public_url.startsWith("/static/")) {
      return `${window.location.origin}${videoInfo.public_url}`;
    }
    return buildApiUrl(
      videoInfo.public_url.startsWith("/api")
        ? videoInfo.public_url
        : `/api${videoInfo.public_url.startsWith("/") ? "" : "/"}${videoInfo.public_url}`,
    );
  }
  if (videoInfo.filename)
    return buildApiUrl(`/api/videos/${videoInfo.filename}`);
  return "";
};

// Log da URL configurada
// VITE_BACKEND_* e REACT_APP_BACKEND_* são opcionais: só entram em uso se VITE_APP_API_URL não estiver definida
// const envVars = {
//   MODE: import.meta.env.MODE,
//   isProduction,
//   VITE_APP_API_URL: import.meta.env.VITE_APP_API_URL,
//   VITE_APP_SOCKET_URL: import.meta.env.VITE_APP_SOCKET_URL,
//   VITE_API_TARGET: import.meta.env.VITE_API_TARGET,
//   VITE_BACKEND_HOST: import.meta.env.VITE_BACKEND_HOST,
//   VITE_BACKEND_PROTOCOL: import.meta.env.VITE_BACKEND_PROTOCOL,
//   VITE_BACKEND_PORT: import.meta.env.VITE_BACKEND_PORT,
//   REACT_APP_BACKEND_HOST: import.meta.env.REACT_APP_BACKEND_HOST,
//   REACT_APP_BACKEND_PROTOCOL: import.meta.env.REACT_APP_BACKEND_PROTOCOL,
//   REACT_APP_BACKEND_PORT: import.meta.env.REACT_APP_BACKEND_PORT,
// };
// const envDefined = Object.fromEntries(
//   Object.entries(envVars).filter(([, v]) => v !== undefined && v !== ""),
// ) as Record<string, string | boolean>;
// console.log("🔧 [API CONFIG] API_BASE_URL:", API_BASE_URL);
// console.log("🔧 [API CONFIG] Environment (definidas):", envDefined);

// Centralização completa das URLs de API
export const API_ENDPOINTS = {
  // Auth endpoints
  AUTH: {
    LOGIN: "/auth/login",
    REGISTER: "/auth/register",
    LOGOUT: "/auth/logout",
    REFRESH: "/auth/refresh",
    ME: "/auth/me",
    /** Health global da API (não é rota do blueprint auth) */
    HEALTH: "/health",
    SET_PASSWORD: "/auth/set-password",
    CONFIRM_EMAIL: "/auth/confirm-email",
    REQUEST_RESET: "/auth/request-reset",
    VALIDATE_RESET_TOKEN: "/auth/validate-reset-token",
    RESET_PASSWORD: "/auth/reset-password",
    CHANGE_PASSWORD: "/auth/change-password",
    UPDATE_PROFILE: "/auth/update-profile",
    PENDING_USERS: "/auth/pending-users",
    APPROVE_USER: "/auth/user/{uuid}/approve",
  },

  // Posts endpoints
  POSTS: {
    LIST: "/posts", // GET /api/posts?status={status}&user_id={user_id}
    PUBLISHED_METRICS: "/posts/published-metrics", // GET /api/posts/published-metrics?post_ids=1,2,3
    COUNTS: "/posts/counts", // GET /api/posts/counts?status={status}&search={search}
    STATS: "/stats",
    CREATE: "/posts", // POST /api/posts (endpoint correto no backend)
    STATUS: "/posts/{id}/status", // POST /api/posts/{id}/status - rota consolidada (approve, reject, publish)
    EDIT: "/posts/{id}/edit",
    SCHEDULE: "/posts/{id}/schedule",
    DELETE: "/posts/{id}/delete",
    ENHANCE: "/posts/{id}/enhance",
    REPUBLISH: "/posts/{id}/republish",
    DISMISS_FAILURE: "/posts/{id}/dismiss-failure",
    SCHEDULED: "/posts/scheduled",
    UPLOAD_VIDEO: "/posts/{id}/upload-video",
    PUBLISH_VIDEO: "/posts/{id}/publish-video",
    PUBLISH_MULTI: "/posts/{id}/publish-multi",
    RESCHEDULE: "/posts/{id}/reschedule",
  },

  // Content endpoints
  CONTENT: {
    LIBRARY: "/content/library",
    LIBRARY_ITEM: "/content/library/{id}",
    PROCESS: "/content/process",
    PROCESS_NAMED: "/content/process-named",
    UPLOAD_FILE: "/content/upload-file",
    GENERATE_CALENDAR: "/content/{id}/generate-calendar",
    KNOWLEDGE_INSIGHTS: "/content/knowledge-insights",
    SEARCH_ENHANCED: "/content/search-enhanced",
    VALIDATE_QUALITY: "/content/validate-quality",
    SCHEDULE_OVERVIEW: "/content/schedule-overview",
    OPTIMIZE_SCHEDULE: "/content/optimize-schedule",
  },

  // Analytics endpoints
  ANALYTICS: {
    PERFORMANCE_DASHBOARD: "/analytics/performance-dashboard",
    ENGAGEMENT_PREDICTION: "/analytics/engagement-prediction",
    OPTIMAL_TIMING: "/analytics/optimal-timing",
    CAMPAIGN_INSIGHTS: "/analytics/campaign-insights",
    INTELLIGENT_SUGGESTIONS: "/analytics/intelligent-suggestions",
    SENTIMENT_ANALYZE: "/analytics/sentiment/analyze",
    HASHTAG_RECOMMENDATIONS: "/analytics/hashtag-recommendations",
    TREND_ANALYSIS: "/analytics/trend-analysis",
    DASHBOARD: "/analytics/dashboard",
    ML_PERFORMANCE: "/analytics/ml-performance",
    STATUS: "/analytics/status",
    ICP_ANALYSIS: "/analytics/icp-analysis",
    ADVANCED_DASHBOARD: "/analytics/advanced-dashboard",
  },

  // ML endpoints
  ML: {
    TRAIN: "/ml/train",
  },

  // Cache endpoints
  CACHE: {
    CLEAR: "/cache/clear",
    STATS: "/cache/stats",
  },

  // Monitoring endpoints
  MONITORING: {
    METRICS: "/monitoring/metrics",
    ALERTS: "/monitoring/alerts",
  },

  // Content Selector endpoints
  CONTENT_SELECTOR: {
    CONTENT_TYPES: "/content/content-types",
    GENERATE_CUSTOM: "/content/generate-custom-content",
    PROCESS_FILE: "/content/process-file",
    PROCESS_CUSTOM: "/content/process-custom",
    GENERATE_SCHEDULE_FROM_SELECTION:
      "/content/generate-schedule-from-selection",
    GENERATE_SOCIAL_MEDIA_POST: "/content/generate-social-media-post",
  },

  // Auto Responder endpoints
  AUTO_RESPONDER: {
    RESPONSES: "/auto-responder/responses",
    TEMPLATES: "/auto-responder/templates",
    STATS: "/auto-responder/stats",
    STATUS: "/auto-responder/status",
    ENABLE: "/auto-responder/enable",
    APPROVE: "/auto-responder/approve/{id}",
    REJECT: "/auto-responder/reject/{id}",
    MONITOR_COMMENTS: "/auto-responder/monitor-comments",
    CUSTOM_TAGS: "/auto-responder/custom-tags",
    CUSTOM_TAG_DELETE: "/auto-responder/custom-tags/{id}",
    SENTIMENT_STATS: "/auto-responder/sentiment-stats",
    PROCESS_COMMENT: "/auto-responder/process-comment",
    ORIGIN_POST: "/auto-responder/responses/{id}/origin-post",
    SUGGESTIONS: "/auto-responder/responses/{id}/suggestions",
    REINDEX_KNOWLEDGE: "/auto-responder/reindex-knowledge",
  },

  // Organization & Client Companies endpoints
  ORGANIZATION: {
    GET: "/organization",
    CREATE: "/organization",
    UPDATE: "/organization",
    CONTEXT_OPTIONS: "/organization/context-options",
    WHATSAPP_CRM_SUBSCRIPTION: "/organization/whatsapp-crm-subscription",
    BRAND_KIT_SUGGESTION: "/organization/brand-kit-suggestion",
    LOGO: "/organization/logo",
    BRAND_KIT_SUGGESTION_DISMISS: "/organization/brand-kit-suggestion/dismiss",
    CLIENT_BRAND_KIT_SUGGESTION: (id: number) =>
      `/organization/clients/${id}/brand-kit-suggestion`,
    CLIENT_BRAND_KIT_SUGGESTION_DISMISS: (id: number) =>
      `/organization/clients/${id}/brand-kit-suggestion/dismiss`,
    SOCIAL_CONFIG: "/organization/social-config",
    SOCIAL_CONFIG_PLATFORM: (platform: string) =>
      `/organization/social-config/${platform}`,
    SOCIAL_CONFIG_VERIFY: (platform: string) =>
      `/organization/social-config/${platform}/verify`,
    CLIENTS: "/organization/clients",
    CLIENTS_OPTIONS: "/organization/clients/options",
    CLIENT: (id: number) => `/organization/clients/${id}`,
    CLIENT_LOGO: (id: number) => `/organization/clients/${id}/logo`,
    CLIENT_SOCIAL_CONFIG: (id: number) =>
      `/organization/clients/${id}/social-config`,
    CLIENT_SOCIAL_CONFIG_PLATFORM: (id: number, platform: string) =>
      `/organization/clients/${id}/social-config/${platform}`,
    CLIENT_SOCIAL_CONFIG_VERIFY: (id: number, platform: string) =>
      `/organization/clients/${id}/social-config/${platform}/verify`,
    CLIENT_WHATSAPP_CRM_SUBSCRIPTION: (id: number) =>
      `/organization/clients/${id}/whatsapp-crm-subscription`,
    CLIENT_WHATSAPP_CRM_SUBSCRIPTION_RESYNC: (id: number) =>
      `/organization/clients/${id}/whatsapp-crm-subscription/resync`,
    WHATSAPP_CRM_SUBSCRIPTION_RESYNC:
      "/organization/whatsapp-crm-subscription/resync",
    CLIENT_WHATSAPP_CRM_SUBSCRIPTION_PRICE: (id: number) =>
      `/organization/clients/${id}/whatsapp-crm-subscription/price`,
    WHATSAPP_CRM_SUBSCRIPTION_PRICE:
      "/organization/whatsapp-crm-subscription/price",
    WHATSAPP_CRM_SEATS: "/organization/whatsapp-crm/seats",
    USERS: "/organization/users",
    USER: (id: number) => `/organization/users/${id}`,
    USER_ROLE: (id: number) => `/organization/users/${id}/role`,
    USER_CLIENT_ACCESS: (id: number) =>
      `/organization/users/${id}/client-access`,
    GOOGLE_DISCOVERY_ACCOUNTS:
      "/organization/social-config/google/discovery/accounts",
    GOOGLE_DISCOVERY_LOCATIONS: (accountName: string) =>
      `/organization/social-config/google/discovery/accounts/${accountName}/locations`,
    CLIENT_GOOGLE_DISCOVERY_ACCOUNTS: (clientId: number) =>
      `/organization/clients/${clientId}/social-config/google/discovery/accounts`,
    CLIENT_GOOGLE_DISCOVERY_LOCATIONS: (
      clientId: number,
      accountName: string,
    ) =>
      `/organization/clients/${clientId}/social-config/google/discovery/accounts/${accountName}/locations`,
    GOOGLE_BUSINESS: {
      CONNECT: (companyId: number) =>
        `/integrations/google/connect?company_id=${companyId}`,
      SYNC: "/integrations/google/sync",
      STATUS: (companyId: number) => `/integrations/google/status/${companyId}`,
      DISCONNECT: "/integrations/google/disconnect",
    },
  },

  // User Profile endpoints
  USER: {
    PROFILE: "/user/profile",
  },

  // Credits (IA, image, post - balance and history)
  CREDITS: {
    BALANCE: "/credits/balance",
    HISTORY: "/credits/history",
    HISTORY_EXPORT: "/credits/history/export",
    PURCHASES: "/credits/purchases",
    USAGE_SUMMARY: "/credits/usage-summary",
    COSTS: "/credits/costs",
    CHECKOUT_URL: "/credits/checkout",
    PLANS: "/credits/plans",
    PRODUCT_PACKAGES: "/credits/product-packages",
  },

  // Settings endpoints
  SETTINGS: {
    SOCIAL_CONFIG: "/social-config",
    SOCIAL_CONFIG_ITEM: "/social-config/{platform}",
    SOCIAL_CONFIG_VERIFY: "/social-config/{platform}/verify",
    SOCIAL_CONFIG_REVEAL_FIELD:
      "/social-config/{platform}/reveal-field/{field}",
    AI_CONFIG: "/ai-config",
    LINKEDIN_CONFIG: "/linkedin/config",
  },

  // Posts additional endpoints
  POSTS_EXTENDED: {
    UPLOAD_IMAGE: "/posts/{id}/upload-image",
    GENERATE_IMAGE: "/posts/{id}/generate-image",
    GENERATE_IMAGE_STATUS: "/posts/{id}/generate-image/status/{jobId}",
    UPLOAD_VIDEO: "/posts/{id}/upload-video",
    GENERATE_VIDEO: "/posts/{id}/generate-video",
    GENERATE_VIDEO_STATUS: "/posts/{id}/generate-video/status/{jobId}",
    ENHANCE_IMAGE: "/posts/{id}/enhance-image",
    ENHANCE_IMAGE_STATUS: "/posts/{id}/enhance-image/status/{jobId}",
    MEDIA_JOB_STATUS: "/posts/media-jobs/{jobId}",
    VISUAL_SUGGESTIONS: "/posts/{id}/visual-suggestions",
    CHECK_GRAMMAR: "/posts/{id}/check-grammar",
  },

  // Ebooks endpoints
  EBOOKS: {
    LIST: "/ebooks/list",
    CREATE: "/ebooks/create",
    DELETE: "/ebooks/{id}/delete",
    GET: "/ebooks/{id}",
    UPDATE: "/ebooks/{id}/update",
    DOWNLOAD: "/ebooks/{id}/download",
    ANALYTICS: "/ebooks/analytics",
  },

  // Leads endpoints
  LEADS: {
    LIST: "/leads/list",
    INTELLIGENCE: "/leads/intelligence",
    FIND_FROM_ICP: "/leads/find-from-icp",
  },

  // Learning endpoints
  LEARNING: {
    DASHBOARD: "/learning/dashboard",
    WEEKLY_PLAN: "/learning/weekly-plan",
  },

  // Images endpoints
  IMAGES: {
    UPLOAD: "/images/upload",
  },

  // Analytics additional endpoints
  ANALYTICS_EXTENDED: {
    COLLECT: "/analytics/sync",
    TRAFFIC: "/analytics/traffic",
    TRAFFIC_DIFFERENTIATION: "/traffic/differentiation",
    ICP_FROM_CONTENT_LIBRARY: "/analytics/icp/from-content-library",
  },

  // Empathy endpoints
  EMPATHY: {
    QUESTIONNAIRE: "/empathy/questionnaire",
    PROCESS_RESPONSES: "/empathy/process-responses",
    MAPS: "/empathy/maps",
    MAP: "/empathy/map",
    MAPS_CURRENT: "/empathy/maps/current",
    EXPORT: "/empathy/export",
    ICP_FROM_CONTENT: "/empathy/icp/from-content",
    ANALYSIS: "/empathy/analysis",
    INSIGHTS: "/empathy/analyze-social-media",
    REFRESH_FROM_ALL_DATA: "/empathy/refresh-from-all-data",
    GENERATE_POST: "/empathy/generate-post",
  },

  // Learning endpoints
  LEARNING_DASHBOARD: {
    DASHBOARD: "/learning/dashboard",
  },

  // Social media endpoints
  SOCIAL: {
    INSTAGRAM_POST_COMMENTS: "/instagram/posts/{id}/comments",
    FACEBOOK_POST_COMMENTS: "/facebook/posts/{id}/comments",
    LINKEDIN_POST_COMMENTS: "/linkedin/posts/comments",
    INSTAGRAM_REPLY: "/instagram/comments/{id}/reply",
    FACEBOOK_REPLY: "/facebook/comments/{id}/reply",
    LINKEDIN_REPLY: "/linkedin/comments/{id}/reply",
  },

  // Attribution endpoints
  ATTRIBUTION: {
    REPORT: "/attribution/report",
    GENERATE_STRATEGY: "/attribution/generate-strategy",
  },

  // Revenue Intelligence (microserviço stateless, exposto via SMM)
  REVENUE: {
    HOT_LEADS: "/api/revenue/hot-leads",
    LEAD_SCORE: "/api/revenue/lead/{id}/score",
    CAMPAIGNS_ATTRIBUTION: "/api/revenue/campaigns/attribution",
    CAMPAIGNS_SUMMARY: "/api/revenue/campaigns/summary",
    META_INSIGHTS: "/api/revenue/campaigns/meta-insights",
    RECALCULATE: "/api/revenue/recalculate/{id}",
    TRACKER_SNIPPET: "/api/revenue/tracker-snippet",
    SITE_TOKENS: "/api/revenue/site-tokens",
  },

  // Traffic intelligence endpoints
  TRAFFIC_INTELLIGENCE: {
    REPORT: "/traffic-intelligence/report",
    EXECUTE_ACTION: "/traffic-intelligence/execute-action/{id}",
  },

  // Chatbot endpoints
  CHATBOT: {
    ICP_START_DISCOVERY: "/chatbot/icp/start-discovery",
    ICP_CHAT: "/chatbot/icp/chat",
    ICP_DISCOVERY: "/chatbot/icp/discovery",
    ICP_DISCOVERY_SESSION: "/chatbot/icp/discovery/session",
  },

  // Agent endpoints
  AGENT: {
    COLLECT_ALL_DATA: "/agent/collect-all-data",
  },
} as const;

// Tags de cache para invalidação seletiva
export const CACHE_TAGS = {
  POSTS: "posts",
  ANALYTICS: "analytics",
  USER_DATA: "user_data",
  AGENT_STATUS: "agent_status",
  CONTENT_LIBRARY: "content_library",
  SCHEDULE: "schedule",
} as const;

// Configurações de paginação padrão
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 10,
  DEFAULT_PAGE_SIZE_SMALL: 6, // Para listas menores (ContentList, EbookList)
  DEFAULT_PAGE: 1,
  MAX_PAGE_SIZE: 100,
} as const;

// Timeouts e intervalos
export const TIMEOUTS = {
  API_REQUEST: 120000, // 120 segundos (2 minutos)
  CACHE_DEFAULT_TTL: 5 * 60 * 1000, // 5 minutos
  CACHE_CLEANUP_INTERVAL: 60 * 1000, // 1 minuto
  TOKEN_REFRESH: 5 * 60 * 1000, // 5 minutos
} as const;

// Tamanhos máximos
export const MAX_SIZES = {
  CACHE_ENTRIES: 500,
  FILE_UPLOAD: 25 * 1024 * 1024, // 25MB
  IMAGE_UPLOAD: 5 * 1024 * 1024, // 5MB
} as const;

// Chaves de localStorage
export const STORAGE_KEYS = {
  ACCESS_TOKEN: "access_token",
  REFRESH_TOKEN: "refresh_token",
  TOKEN: "token", // Chave antiga para compatibilidade
  USER: "user",
  ACTIVE_CONTEXT: "active_context", // { type: 'ORGANIZATION' | 'CLIENT_COMPANY', clientCompanyId?: number }
} as const;

// Valores padrão de agendamento
export const SCHEDULE_DEFAULTS = {
  DEFAULT_TIME: "09:00",
  DEFAULT_HOUR: 9,
  DEFAULT_MINUTE: 0,
} as const;

// Configurações de cache
export const CACHE_CONFIG = {
  DEFAULT_TTL: 5 * 60 * 1000, // 5 minutos
  CLEANUP_INTERVAL: 60 * 1000, // 1 minuto
  MAX_SIZE: 500,
  POSTS_TTL: 2 * 60 * 1000, // 2 minutos para posts
  POSTS_APPROVED_TTL: 5 * 60 * 1000, // 5 minutos para posts aprovados
  POSTS_PUBLISHED_TTL: 2 * 60 * 1000, // 2 minutos para posts publicados
  POSTS_STATS_TTL: 1 * 60 * 1000, // 1 minuto para estatísticas
  ANALYTICS_DASHBOARD_TTL: 5 * 60 * 1000, // 5 minutos para dashboard
  ANALYTICS_PREDICTIONS_TTL: 10 * 60 * 1000, // 10 minutos para previsões
  ANALYTICS_INSIGHTS_TTL: 15 * 60 * 1000, // 15 minutos para insights
} as const;
